<template>
  <div>
    <table class="responsive-table">
      <thead>
        <tr>
          <column-header
            v-for="col in columns"
            :key="col.id"
            :label="col.display_label"
            :sorted="col.id === pagination.sortedColumn"
            :sorted-asc="pagination.sortedAsc"
            @sort="onSort(col.id)"
          />

          <th /><!-- Header for New/Current Patient column -->
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in appointments" :key="row.id">
          <td>
            {{ row.counselorName }}
            <label class="d-block d-md-none">
              Counselor:
            </label>
          </td>

          <td>
            {{ row.studentDisplayName }}
            <label class="d-block d-md-none">
              Student:
            </label>
          </td>

          <td>
            {{ row.college.name }}
            <label class="d-block d-md-none">
              College:
            </label>
          </td>

          <td>
            {{ row.start_time | date }}
            <label class="d-block d-md-none">
              Start Time:
            </label>
          </td>

          <td>
            {{ row.existing_patient ? 'Current Client' : 'New Client' }}
          </td>
        </tr>
      </tbody>
    </table>

    <pagination
      v-if="count > pagination.limit"
      :filtered-count="count"
      :page="pagination.page"
      :page-size="pagination.limit"
      :on-page-changed="onPageChanged"
    />
  </div>
</template>

<script>
import ColumnHeader from '@/components/tables/ColumnHeader.vue';
import Pagination from '@/components/Pagination.vue';

export default {
  name: 'AdminRequestsTable',
  components:{
    ColumnHeader,
    Pagination,
  },
  props: {
    count:{
      type: Number,
      require: true
    },
    appointments: {
      type: Array,
    },
    pagination: {
      type: Object,
      default: {
        sortedColumn: 'start_time',
        sortedAsc: false,
        limit: 10,
        page: 1
      }
    }
  },
  data() {
    return {
      columns: [
        { id: 'counselor__user__last_name', display_label: 'Counselor' },
        { id: 'user__last_name', display_label: 'Student' },
        { id: 'user__school_user__school__name', display_label: 'College' },
        { id: 'start_time', display_label: 'Start Time' },
      ],
    };
  },
  methods:{
    onSort(id) {
      const emited = { ...this.pagination }
      if (id && id === emited.sortedColumn) {
          emited.sortedAsc = !emited.sortedAsc;
      } else {
          emited.sortedColumn = id;
          emited.sortedAsc = true;
      }
      this.$emit('paginationChange', emited)
    },
    onPageChanged(page) {
      const emited = { ...this.pagination }
      if (page !== emited.page) {
        emited.page = page;
        this.$emit('paginationChange', emited)
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.responsive-table {
  @include border-radius($border-radius);

  width: 100%;
  border: none;

  thead {
    background: none;
    color: white;

    th {
      position: relative;
      padding: 0.75rem;
      padding-right: $gutter;
      white-space: nowrap;
      border-bottom: 2px solid rgba(255, 255, 255, 0.25);
      cursor: pointer;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;

      i,
      svg {
        position: absolute;
        top: 17px;
        right: 10px;
      }

      .fa-sort {
        z-index: 1;
        opacity: 0.2;
      }

      span {
        .fa-sort-up {
          z-index: 2;
          opacity: 0;
        }

        .fa-sort-down {
          z-index: 3;
          opacity: 0;
        }

        &.active-down {
          .fa-sort-down {
            opacity: 1;
          }
        }

        &.active-up {
          .fa-sort-up {
            opacity: 1;
          }
        }
      }

      &:hover {
        .fa-sort {
          opacity: 0.3;
        }

        span {
          .fa-sort-down {
            opacity: 1;
          }

          &.active-down {
            .fa-sort-down {
              opacity: 1;
            }

            .fa-sort-up {
              opacity: 0;
            }
          }

          &.active-up {
            .fa-sort-up {
              opacity: 1;
            }

            .fa-sort-down {
              opacity: 0;
            }
          }
        }
      }
    }
  }

  tbody {
    tr {
      td {
        padding: 0.75rem;
        color: rgba(white, 0.87);
        border-top: 1px solid rgba(255, 255, 255, 0.25);

        a {
          color: darken($highlight-color, 5%);
        }
      }
    }
  }

  .actions {
    text-align: right;
  }

  @media (max-width: $sm-max) {
    display: block;
    border: none;

    thead,
    tbody,
    th,
    td,
    tr {
      display: block;
    }

    tbody {
      tr {
        background: $dark-blue;

        td {
          padding: 0.75rem;
          text-align: right;
          border-bottom: none;
          border-top: 1px solid rgba(255, 255, 255, 0.25);

          &:last-of-type {
            border-bottom: none;
          }
        }
      }
    }

    thead tr {
      position: absolute;
      top: -9999px;
      left: -9999px;
    }

    tr {
      @include border-radius($border-radius);

      margin: 0 0 1rem 0;
      background: white;
    }

    td {
      position: relative;
      padding-left: 50%;
      border: none;
      border-bottom: $secondary-border;

      label {
        position: absolute;
        top: 12.5px;
        left: 15px;
        width: 45%;
        padding-right: 10px;
        font-weight: bold;
        white-space: nowrap;
        text-align: left;
      }
    }

    .btn-ghost {
      border-color: $primary-color;
      background: $primary-color;
      color: $btn-color;
      height: 45px;

      &:hover,
      &:focus {
        border-color: darken($primary-color, 5%);
        background-color: darken($primary-color, 5%);
        color: $btn-color;
      }
    }
  }

  .icons {
    color: $green;
    text-align: right;
  }
}

.fa-btn {
  background: none;
  color: white;
  border: none;
  box-shadow: none;
}
</style>
